import { AsyncPipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatSelectionListChange } from '@angular/material/list';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable } from 'rxjs';
import { FilterBase } from 'src/app/shared/grid/filter-base';
import { GridFilterPopupComponent } from 'src/app/shared/grid/filters/base-filter-popup.component';
import { GRID_MATERIAL_PROVIDERS } from 'src/app/shared/grid/grid-material-control-defaults.const';
import { ExpertiseLateral, Specialty } from 'src/app/shared/models/lookup-profession.model';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';

function toSpecialties(specialties: any[]): Specialty[] {
    return (
        specialties?.map((item) => {
            const specialty: Specialty = {
                id: item?.id ?? null,
                abbreviation: item?.abbreviation ?? '',
                name: item?.name ?? '',
                specialtyRelatedSpecialties: item?.specialtyRelatedSpecialties ?? [],
                professionIds: item?.professionIds ?? [],
                expertiseLaterals: item?.expertiseLaterals ?? []
            };

            item = specialty;
            return item;
        }) ?? []
    );
}

@UntilDestroy()
@Component({
    selector: 'grid-specialty-filter-popup',
    template: ` <grid-filter-popup
        [isFilterActive]="isFilterActive"
        [width]="width"
        (onResetClick)="onResetClick()"
        (onFilterClick)="onFilterClick()"
        (onClose)="onPopupClose()">
        <mat-form-field>
            <div class="ud-configuration__filter-popup__mat-form-field__textbox">
                <input
                    #specSearch
                    matInput
                    placeholder="Search"
                    (keyup)="specialtiesSearch$.next(specSearch.value)" />
            </div>
        </mat-form-field>
        @if (showAllOption) {
            <div>
                <div class="ud-configuration__filter-popup__mat-form-field__checkbox">
                    <mat-checkbox
                        (change)="selectAllClick($event)"
                        [(ngModel)]="selectAll"
                        [(indeterminate)]="selectAllIndeterminate"
                        labelPosition="after">
                        Select All
                    </mat-checkbox>
                </div>
            </div>
            <mat-divider class="divider"></mat-divider>
        }
        @if (useRelatedSpecialtiesFilter) {
            <div>
                <div class="ud-configuration__filter-popup__mat-form-field__checkbox">
                    <mat-checkbox
                        color="primary"
                        [(ngModel)]="includeRelatedSpecialties"
                        (ngModelChange)="selectRelatedSpecialtyIds()"
                        labelPosition="after">
                        Include Related Specialties
                    </mat-checkbox>
                </div>
                <mat-divider class="divider"></mat-divider>
            </div>
        }
        <div class="list-container">
            <mat-selection-list (selectionChange)="addOption($event)">
                @for (spec of specialties$ | async; track spec.id; let i = $index) {
                    <mat-list-option
                        color="primary"
                        [value]="spec.id"
                        [selected]="selectedSpecialties?.includes(spec.id)">
                        <div
                            ><span
                                ><strong>{{ spec.name }}</strong>
                                {{ spec.abbreviation ? '(' + spec.abbreviation + ')' : '' }}</span
                            ></div
                        >
                        @if (spec.expertiseLaterals?.length) {
                            <div class="related-specs"> ({{ getExpertiseLaterals(spec.expertiseLaterals) }}) </div>
                        }
                    </mat-list-option>
                }
            </mat-selection-list>
        </div>
    </grid-filter-popup>`,
    styleUrls: ['./grid-specialty-filter-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    providers: [...GRID_MATERIAL_PROVIDERS],
    imports: [
        MatListModule,
        AsyncPipe,
        MatCheckboxModule,
        FormsModule,
        GridFilterPopupComponent,
        MatFormFieldModule,
        MatInputModule
    ]
})
export class GridSpecialtyFilterPopupComponent extends FilterBase implements OnInit, OnChanges {
    @Input() width = 400;
    @Input({ transform: toSpecialties }) specialties: Specialty[] = [];
    @Input() showAllOption = false;
    @Input() useRelatedSpecialtiesFilter = false;
    @Output() selectedValues = new EventEmitter<any>();

    selectAllIndeterminate = false;
    selectAll = false;
    specialtiesSearch$ = new BehaviorSubject<string>(null);
    specialties$: Observable<Specialty[]>;
    submitOnChange = false;
    includeRelatedSpecialties: boolean;
    selectedSpecialties: any[];
    userSelectedOptions: any[] = [];

    constructor() {
        super();
    }

    get isFilterActive(): boolean {
        return (
            !isEmpty(this.selectedSpecialties) &&
            this.selectedSpecialties.length > 0 &&
            !isEmpty(this.selectedSpecialties[0])
        );
    }

    ngOnInit() {
        this.selectedSpecialties = this.getLatestFilterValue() || [];
        this.specialties$ = new Observable((x) => {
            this.specialtiesSearch$.subscribe((specText) =>
                x.next(
                    this.specialties.filter((x) =>
                        specText
                            ? `${x.name} (${x.abbreviation})`.toLowerCase().indexOf(specText.toLowerCase()) > -1
                            : true
                    )
                )
            );
        });
        this.updateSelectAllStatus();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['filter']) {
            this.selectedSpecialties = this.getLatestFilterValue();
            if (this.selectedSpecialties === null) {
                this.userSelectedOptions = [];
            }
        }
        if (changes['specialties']) {
            this.specialtiesSearch$.next('');
        }
    }

    addOption(item: MatSelectionListChange) {
        this.userSelectedOptions = this.userSelectedOptions || [];
        const ix = this.userSelectedOptions.indexOf(item.options[0].value);
        if (ix >= 0) {
            this.userSelectedOptions.splice(ix, 1);
        } else {
            this.userSelectedOptions.push(item.options[0].value);
        }
        this.selectRelatedSpecialtyIds();
    }

    selectRelatedSpecialtyIds(): void {
        if (!this.userSelectedOptions || this.userSelectedOptions.length === 0) {
            this.selectedSpecialties = [];
        } else if (this.includeRelatedSpecialties) {
            // find all related ones based on current selected options
            const related = [];
            this.userSelectedOptions.forEach((specialtyId) => {
                const expertiseLaterals = this.specialties.find((s) => s.id === specialtyId)?.expertiseLaterals;
                const lateralSpecialtyIds = expertiseLaterals?.map((x) => x.lateralExpertiseId);
                lateralSpecialtyIds?.forEach((rsid) => {
                    if (!related.includes(rsid)) {
                        related.push(rsid);
                    }
                });
            });

            // merge all
            this.selectedSpecialties = Array.from(
                new Set([...this.selectedSpecialties, ...related, ...this.userSelectedOptions])
            );
        } else {
            // remove related
            this.selectedSpecialties = [...this.userSelectedOptions];
        }
        this.updateSpecialtyFilter();
    }

    updateSelectAllStatus() {
        if (!this.selectedSpecialties || this.selectedSpecialties.length === 0) {
            this.selectAll = false;
            this.selectAllIndeterminate = false;
        } else if (this.selectedSpecialties.length === this.specialties.length) {
            this.selectAll = true;
            this.selectAllIndeterminate = false;
        } else {
            this.selectAllIndeterminate = true;
        }
    }

    getExpertiseLaterals(expertiseLaterals: ExpertiseLateral[]): string {
        return this.specialties
            .filter((x) => expertiseLaterals.map((t) => t.lateralExpertiseId).indexOf(x.id) > -1)
            .map((x) => x.abbreviation)
            .join(', ');
    }

    selectAllClick($event: MatCheckboxChange) {
        this.selectedSpecialties = $event.checked ? this.specialties.map((x) => x.id) : [];
        this.updateSpecialtyFilter();
    }

    updateSpecialtyFilter(): void {
        this.trimFilterByField(this.filter, this.field);
        if (this.field)
            this.updateFilter({
                field: this.field,
                operator: 'eq',
                value: this.selectedSpecialties
            });

        this.updateSelectAllStatus();
    }

    onPopupClose(): void {
        if (this.isChanged) {
            this.submitFilter();
            this.selectedValues.emit(this.selectedSpecialties);
        }
    }

    onResetClick(): void {
        this.includeRelatedSpecialties = false;
        this.trimFilterByField(this.filter, this.field);
        this.submitFilter();
        this.selectedValues.emit([]);
    }

    onFilterClick(): void {
        this.submitFilter();
        this.selectedValues.emit(this.selectedSpecialties);
    }
}
